import { useRef, useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AnimatePresence, Reorder, motion } from 'motion/react';
import { SwiperClass } from 'swiper/react';
import { Menu as MenuIcons, Headphones, UserRound } from 'lucide-react';

import { Menu, ProfileMenu } from '@/components/menu/Menu';
import { Button, LanguageOption } from '../menu-button/MenuButton';

import config from '@/config';
import { useBackUrl } from '@/App.hooks';
import { AppContext } from '@/App.context';
import { ArticleRelevance } from '@/utils/ArticleRelevance';
import {
	NavbarIconLogo,
	Article,
	NavbarVisibility,
	LANGUAGES,
	ISSUE_LANG_KEY,
} from '@/constants';
import { AssetsContext } from '@/assets/Assets.context';

import { TrackingContext as TrackerContext } from '@/tracking/Tracking.context';
import { TrackingData } from '@/tracking/Tracking.events';

import { Icon } from '@/components/icon/Icon';
import { Articles } from '@/components/article/Articles';
import { NavigationBar } from '@/components/navigation-bar/NavigationBar';
import { AudioPlayerContext } from '@/components/player/audio/AudioPlayer.context';
import { BasicPlayer } from '@/components/player/audio/skins/basic/BasicPlayer';

import { useDeviceInformation } from '@/hooks/use-element';
import { useSearch } from '@/hooks/use-search';
import { useKeyPressShortcut } from '@/hooks/use-shortcut';
import { useIsSmall } from '@/hooks/use-media-query';

import styles from './Responsive.module.css';

import { useInternalLink, useLoad } from './Responsive.hooks';
import { ResponsiveContext } from './Responsive.context';
import { IssueContext } from '../Issue.context';

import { isReplicaSupported } from '../replica/Replica.hooks';

const parentVariants = {
	/** Define the "visible" state and its styles **/
	visible: { gridTemplateRows: 'var(--top-bar-height) 1fr' },
	/** Define the "hidden" state and its styles **/
	hidden: { gridTemplateRows: '0px 1fr' },
};

export const ResponsiveView = () => {
	const [articleIndexMap, setArticleIndexMap] = useState<Map<string, number>>(
		new Map(),
	);
	const { sendEvent } = useContext(TrackerContext);
	const {
		issue,
		setCurrentArticle,
		language,
		setMode,
		articles,
		fontSize,
		currentArticle,
		setFontSize,
		favorites,
		setFavorites,
		languages,
		disabledLanguages,
		setLanguage,
	} = useContext(IssueContext);
	const device = useDeviceInformation();
	const [index, setIndex] = useState<null | number>(null);
	const [search, setSearch] = useState('');
	const [swiper, setSwiper] = useState<any>(null);
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isProfileMenuOpen, setIsProfileMenuOpen] = useState<boolean>(false);
	const total = issue ? issue.articles.length : 0;
	const [scrollDirection] = useState<undefined | 'up' | 'down'>();
	const [navbarVisibility, setNavbarVisibility] = useState<NavbarVisibility>(
		'visible',
	);

	const languageOptions = useMemo<Array<LanguageOption>>(() => {
		return [...languages].map(l => {
			return {
				name: LANGUAGES?.[language]?.[l],
				value: l,
				disabled: disabledLanguages.has(l),
			};
		});
	}, [currentArticle, language, disabledLanguages]);

	useEffect(() => {
		if (!issue) {
			return;
		}

		if (config.issue.contentType === 'replica') {
			setMode('replica');
			return;
		}
	}, [issue]);

	useEffect(() => {
		if (!articles) {
			return;
		}
		const articleMap = articles.reduce(
			(acc: Map<string, number>, article: Article, index: number) => {
				acc.set(article.id, index);
				return acc;
			},
			new Map<string, number>(),
		);
		setArticleIndexMap(articleMap);
	}, [articles]);

	useLoad(issue, setIndex);

	useEffect(() => {
		if (!issue) {
			return;
		}
		if (!swiper) {
			return;
		}
		if (swiper.destroyed) {
			return;
		}

		if (index === null) {
			return;
		}

		swiper.slideTo(index, 750);
		const article = clone(issue.articles[index]);
		if (window.location.hash) {
			const hash = `${window.location.hash}`;
			setTimeout(() => {
				scrollToElementId(article.id, hash);
			}, 1000);
		}
		setCurrentArticle(article);
	}, [swiper, index, issue]);

	const onNextArticleClick = () => {
		if (index === null) return;
		if (index + 1 >= total) {
			return;
		}
		setIndex(index + 1);
	};

	const onPrevArticleClick = () => {
		if (index === null) return;
		if (index === 0) {
			return;
		}
		setIndex(index - 1);
	};

	useKeyPressShortcut(
		['ArrowRight'],
		isSidebarOpen ? () => {} : onNextArticleClick,
	);
	useKeyPressShortcut(
		['ArrowLeft'],
		isSidebarOpen ? () => {} : onPrevArticleClick,
	);

	const classNames = useMemo(() => {
		const response: Array<any> = [styles['issue']];
		if (isSidebarOpen) {
			response.push(styles['sidebar-open']);
		}
		if (!issue) {
			return response;
		}
		if (!swiper) {
			return response;
		}
		if (swiper.destroyed) {
			return response;
		}

		/*if (scrollDirection === 'down') {
			response.push(styles['hide-scroll-direction']);
			// swiper.allowTouchMove = false;
		} else {
			// swiper.allowTouchMove = true;
		}*/
		return response;
	}, [isSidebarOpen, scrollDirection, swiper]);

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	const toggleSidebar = () => {
		sendEvent({
			type: !isSidebarOpen ? 'OPEN_TOC' : 'CLOSE_TOC',
		});
		setIsSidebarOpen(!isSidebarOpen);
	};

	const onTransitionEnd = (s: SwiperClass) => {
		if (!s || !swiper) {
			return;
		}
		const { activeIndex } = s;
		setIndex(activeIndex);
		/*swiper.slideTo(activeIndex, 750, false);
		const article = clone(issue.articles[activeIndex]);
		if (!article) {
			return;
		}
		setNavbarVisibility('visible');
		setCurrentArticle(article);*/
	};

	return (
		<ResponsiveContext.Provider
			value={{
				swiper,
				setSwiper,
				isMenuOpen,
				setIsMenuOpen,
				isSidebarOpen,
				setIsSidebarOpen,
				toggleSidebar,
				toggleMenu,
				total,
				onTransitionEnd,
				onNextArticleClick,
				onPrevArticleClick,
				index,
				setIndex,
				device,
				search,
				setSearch,
				setScrollDirection: () => {},
				scrollDirection,
				articleIndexMap,
				navbarVisibility:
					config.app.hideNavbarOnScroll === false
						? 'visible'
						: navbarVisibility,
				setNavbarVisibility,
				sendEvent,
				isProfileMenuOpen,
				setIsProfileMenuOpen,
			}}
		>
			<motion.div
				variants={parentVariants}
				animate={navbarVisibility}
				transition={{
					ease: [0.1, 0.25, 0.3, 1],
					duration: 0.22,
				}}
				className={classNames.join(' ')}
			>
				<Topbar />
				<Sidebar />
				<Container />
			</motion.div>
			<Menu isOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
				{config?.app?.menu?.externalUrl?.enabled ? (
					<Button.WebsiteButton
						url={config?.app?.menu?.externalUrl.url}
						title={config?.app?.menu?.externalUrl.title}
					/>
				) : (
					<></>
				)}
				<Button.ShareArticle
					article={currentArticle}
					language={language}
					onShare={(article, method = 'os') => {
						const eventData: TrackingData = {
							method,
							contentType: 'article',
							article: {
								id: parseInt(`${article.id}`, 10),
								ArticleID: parseInt(`${article.ArticleID}`, 10),
							},
						};
						sendEvent({
							type: 'SHARE',
							data: eventData,
						});
					}}
				/>
				<Button.Library />
				{issue.pages?.length && isReplicaSupported(issue) ? (
					<Button.Replica
						onClick={() => {
							setMode('replica');
							sendEvent({
								type: 'BTN_CLICK_REPLICA',
								location: window.location.pathname,
								data: {
									lang: language,
									issue: {
										id: parseInt(`${issue.id}`, 10),
										title: issue.name,
										slug: issue.slug,
									},
									article: {
										id: parseInt(
											`${currentArticle.id}`,
											10,
										),
										ArticleID: parseInt(
											`${currentArticle.ArticleID}`,
											10,
										),
										slug: currentArticle.slug,
										title: currentArticle.name[language],
									},
								},
							});
						}}
					/>
				) : (
					<></>
				)}
				{issue?.externalUrl && (
					<Button.ExternalUrl
						onClick={() => {
							const { externalUrl } = issue;
							const url = externalUrl;
							const fileName = url.split('/').pop();
							const link = document.createElement('a');
							link.href = url;
							link.download = fileName
								? `${fileName}`
								: 'file.pdf';
							link.target = '_blank';
							document.body.appendChild(link);
							link.click();
							document.body.removeChild(link);
						}}
					/>
				)}
				<Button.Font fontSize={fontSize} setFontSize={setFontSize} />

				<Button.Links links={config?.app?.menu?.links} />

				<Button.Language
					language={language}
					options={languageOptions}
					onSelectLanguage={option => {
						const { value } = option;
						setLanguage(value);
					}}
				/>
				<Button.About />

				<Button.Favorite
					article={currentArticle}
					favorites={favorites}
					setFavorites={setFavorites}
				/>
			</Menu>
			<ProfileMenu
				open={isProfileMenuOpen}
				setOpen={setIsProfileMenuOpen}
			/>
		</ResponsiveContext.Provider>
	);
};

function Sidebar() {
	const ref = useRef(null);
	return (
		<aside ref={ref} className={styles['sidebar']}>
			<SearchBar />
			<Toc />
		</aside>
	);
}

function SearchBar() {
	const { isSidebarOpen, setIsSidebarOpen, sendEvent } = useContext(
		ResponsiveContext,
	);

	return (
		<div className={styles['search-bar']}>
			<button
				onClick={() => {
					sendEvent({
						type: !isSidebarOpen ? 'OPEN_TOC' : 'CLOSE_TOC',
					});
					setIsSidebarOpen(!isSidebarOpen);
				}}
			>
				<Icon
					id={'sidebar-btn'}
					name={'sidebar'}
					color={isSidebarOpen ? '#000' : '#333'}
				/>
			</button>
			<SearchInput />
		</div>
	);
}

function SearchInput() {
	const { search, setSearch } = useContext(ResponsiveContext);
	const onChange = (e: any) => {
		setSearch(e.target.value);
	};

	return (
		<div className={styles['search-input']}>
			<input
				placeholder="Search"
				value={search}
				onChange={onChange}
				type="text"
			/>
			{search ? <ClearSearchButton /> : null}
		</div>
	);
}

function ClearSearchButton() {
	const { setSearch } = useContext(ResponsiveContext);
	return (
		<motion.button
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
			onClick={() => {
				setSearch('');
			}}
		>
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="15"
				height="15"
				viewBox="0 0 24 24"
				fill="none"
				stroke={'#c3c3c3'}
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
				className="feather feather-x-circle"
			>
				<circle cx="12" cy="12" r="10"></circle>
				<line x1="15" y1="9" x2="9" y2="15"></line>
				<line x1="9" y1="9" x2="15" y2="15"></line>
			</svg>
		</motion.button>
	);
}

function Toc() {
	const { issue, articles, currentArticle, language } = useContext(
		IssueContext,
	);
	const { search } = useContext(ResponsiveContext);
	const searchResults = useSearch({
		search,
		items: articles.map(article => {
			return {
				id: article.id,
				title: article.name[language],
				text: ArticleRelevance.getArticleCorpus(article, language).join(
					' ',
				),
			};
		}),
	});

	if (!issue || !currentArticle) {
		return <ul className={styles['toc']} />;
	}

	let filteredArticles = articles
		.filter(removeExcludedArticles)
		.filter((a: Article) => {
			if (!searchResults) return true;
			return searchResults.has(`${a.id}`);
		});

	// If there are search results sort them by their scores
	if (searchResults) {
		filteredArticles = filteredArticles.sort((a, b) => {
			const aResult = searchResults.get(`${a.id}`)!;
			const bResult = searchResults.get(`${b.id}`)!;

			return bResult.score - aResult.score;
		});
	}

	return (
		<AnimatePresence>
			<Reorder.Group
				values={filteredArticles}
				className={styles['toc']}
				onReorder={() => {
					console.log(`ITEMS reordered`);
				}}
			>
				{filteredArticles.map((article: any) => {
					const { id, name, thumbnail, score } = article;
					return (
						<TocItem
							id={id}
							key={`list-${id}`}
							thumbnail={thumbnail}
							title={name['en']}
							score={score}
							isActive={currentArticle.id === id}
						/>
					);
				})}
			</Reorder.Group>
		</AnimatePresence>
	);
}

function removeExcludedArticles(article: any) {
	if (article.role === 'advert') {
		return false;
	}
	return article?.channel?.toc === 'excluded' ? false : true;
}

function TocItem({ title, thumbnail, isActive, id, score }: TocItemProps) {
	const {
		setIndex,
		device,
		setIsSidebarOpen,
		articleIndexMap,
		sendEvent,
	} = useContext(ResponsiveContext);
	const onClick = () => {
		if (device && device.type === 'mobile') {
			sendEvent({
				type: 'CLOSE_TOC',
			});
			setIsSidebarOpen(false);
		}
		const selectedIndex = articleIndexMap.get(id);
		if (selectedIndex !== undefined) {
			setIndex(selectedIndex);
		}
	};

	return (
		<Reorder.Item
			key={`item-${id}`}
			value={`item-${id}`}
			dragListener={false}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			data-score={score && isNaN(score) ? undefined : score}
			exit={{ opacity: 0, height: 0 }}
			className={isActive ? styles['active'] : ''}
			onClick={onClick}
		>
			<div>
				<img
					title={`${title} - Thumbnail`}
					alt={`${title} - Thumbnail`}
					src={thumbnail}
				/>
			</div>
			<span>{title}</span>
		</Reorder.Item>
	);
}

interface TocItemProps {
	id: string;
	title: string;
	thumbnail: string;
	isActive: boolean;
	score?: number;
}

const topbarParentVariants = {
	visible: { opacity: 1 },
	/** Define the "hidden" state and its styles **/
	hidden: { opacity: 0 },
};

function Topbar() {
	const {
		isSidebarOpen,
		setIsSidebarOpen,
		navbarVisibility,
		sendEvent,
	} = useContext(ResponsiveContext);

	return (
		<motion.nav
			variants={topbarParentVariants}
			animate={navbarVisibility}
			transition={{
				ease: [0.1, 0.25, 0.3, 1],
				duration: 0.2,
			}}
			className={styles['topbar']}
		>
			<div>
				<BackButton />
				<button
					className="tooltip down"
					data-text={isSidebarOpen ? 'Close' : 'Open'}
					onClick={() => {
						sendEvent({
							type: !isSidebarOpen ? 'OPEN_TOC' : 'CLOSE_TOC',
						});
						setIsSidebarOpen(!isSidebarOpen);
					}}
				>
					<Icon
						id={'sidebar-btn'}
						name={'sidebar'}
						color={
							isSidebarOpen
								? 'var(--app-accent-color)'
								: '#c3c3c3'
						}
					/>
				</button>
			</div>
			<div className={styles['logo']}>
				<NavbarIconLogo />
			</div>
			<div>
				<PlayerButton />
				<MenuButton />
				<ProfileButton />
			</div>
		</motion.nav>
	);
}

function ProfileButton() {
	const { setIsProfileMenuOpen } = useContext(ResponsiveContext);
	if (!config?.app?.auth?.enabled) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}
	return (
		<button
			type="button"
			className="tooltip down"
			data-text={'Profile'}
			onClick={() => {
				setIsProfileMenuOpen(true);
			}}
		>
			<UserRound color={'#c3c3c3'} size={24} />
		</button>
	);
}

const BackButton = () => {
	const url = useBackUrl();
	const navigate = useNavigate();
	const { issue } = useContext(IssueContext);
	if (!issue) {
		return null;
	}

	return (
		<button
			id="back-button"
			type="button"
			className="tooltip down"
			data-text="Back"
			onClick={() => {
				navigate({
					to: url,
					replace: true,
				});
			}}
		>
			<Icon id={'back-btn'} name={'arrow-left'} color={'#c3c3c3'} />
		</button>
	);
};

const PlayerButton = () => {
	const classNames = ['tooltip', 'down', styles['player-btn']];
	const { play, setAlbum, setTitle, setArtist, setArtwork } = useContext(
		AudioPlayerContext,
	);
	const { language, currentArticle, setAudioPlayerUrl, issue } = useContext(
		IssueContext,
	);
	if (!currentArticle.audios) {
		return null;
	}

	if (!currentArticle.audios.length || !language) {
		return null;
	}

	const audio = currentArticle.audios
		?.filter((a: any) => a.lang === language)
		.pop();

	if (!audio) {
		return null;
	}

	const { name, author, thumbnail } = currentArticle;
	const title = name[language];
	const artist = author || undefined;

	const { url } = audio;

	return (
		<button
			type="button"
			className={classNames.join(' ')}
			data-text={'Play Article'}
			onClick={() => {
				setAudioPlayerUrl(url);
				setAlbum(issue.name);
				setTitle(title);
				setArtist(artist);
				setArtwork(thumbnail ? [{ src: thumbnail }] : undefined);
				setTimeout(() => {
					console.log(currentArticle);
					console.log({
						album: issue.name,
						title,
						artist,
					});

					/*getArtwork(thumbnail)
						.then((artwork) => {
							console.log(`Artwork: `, artwork);

						})
						.catch(console.error);*/

					play();
				}, 1000);
			}}
		>
			<Headphones size={24} color={'#c3c3c3'} />
		</button>
	);
};

function MenuButton() {
	const { toggleMenu } = useContext(ResponsiveContext);
	return (
		<button
			type="button"
			className="tooltip down"
			data-text={'Actions'}
			onClick={() => {
				toggleMenu();
			}}
		>
			<MenuIcons color="#c3c3c3" />
		</button>
	);
}

function Container() {
	const { onNextArticleClick, index, total, onPrevArticleClick } = useContext(
		ResponsiveContext,
	);

	return (
		<div className={styles['container']}>
			<Wrapper />
			<IssueCover />
			{total > 1 ? (
				<NavigationBar
					classNames={[styles['navigation-bar']]}
					onNextClick={onNextArticleClick}
					onPrevClick={onPrevArticleClick}
					disable={{
						left: index === 0,
						right: index === total - 1,
					}}
				/>
			) : null}
			<Player />
		</div>
	);
}

function Player() {
	const isSmall = useIsSmall();
	const { isReady, url } = useContext(AudioPlayerContext);

	if (!isReady || !url) {
		return null;
	}

	const variants = isSmall
		? {
				animate: {
					width: '100vw',
					marginLeft: 0,
					marginRight: 0,
					marginBottom: 0,
					borderBottomLeftRadius: 0,
					borderBottomRightRadius: 0,
				},
		  }
		: {
				animate: {
					width: 500,
					marginLeft: 20,
					marginRight: '0vw',
					marginBottom: 'var(--bottom-margin-bottom)',
					borderBottomLeftRadius: 8,
					borderBottomRightRadius: 8,
				},
		  };

	return (
		<motion.div
			variants={variants}
			animate="animate"
			className={styles['player-container']}
		>
			<BasicPlayer show={true} />
		</motion.div>
	);
}

function Wrapper() {
	return (
		<div className={styles['wrapper']}>
			<Content />
		</div>
	);
}

function Content() {
	const contentRef = useInternalLink();
	const { device } = useContext(AppContext);
	const { stylesMap } = useContext(AssetsContext);
	const { articles, fontSize, language, navigateToArticle } = useContext(
		IssueContext,
	);
	const {
		swiper,
		setSwiper,
		onTransitionEnd,
		index,
		setScrollDirection,
		setNavbarVisibility,
	} = useContext(ResponsiveContext);
	return (
		<div ref={contentRef}>
			{index === null ? null : (
				<Articles
					lang={language}
					fontSize={fontSize}
					styles={stylesMap}
					articles={articles}
					isTouchDevice={device?.isTouchable}
					index={index}
					virtual={true}
					device={device?.type ?? 'mobile'}
					onTransitionEnd={onTransitionEnd}
					swiper={swiper}
					navigateToArticle={navigateToArticle}
					setSwiper={setSwiper}
					setScrollDirection={setScrollDirection}
					setNavbarVisibility={setNavbarVisibility}
				/>
			)}
		</div>
	);
}

function IssueCover() {
	const { issue } = useContext(IssueContext);
	if (!issue) {
		return null;
	}

	if (!issue.cover) {
		return null;
	}

	/*
		Disable IssueCover
		return (
		<div>
			<img src={issue.cover} alt={'Cover'} />
		</div>
	);
	*/

	return null;
}

function clone(obj: any) {
	if (structuredClone) {
		return structuredClone(obj);
	}
	return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}

function scrollToElementId(articleId: string, id: string) {
	const articleElement = document.getElementById(`article-${articleId}`);
	if (!articleElement) {
		return;
	}
	const el = articleElement.querySelector(id);

	if (!el) {
		return;
	}

	el.scrollIntoView({
		behavior: 'smooth',
		block: 'start',
		inline: 'center',
	});

	const prevUrl = `${window.location.pathname}`;

	history.replaceState({}, document.title, prevUrl);
}
